/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { css } from '@emotion/react'
import { useState, type FunctionComponent, type ReactNode } from 'react'
import React from 'react'

import {
  EuiButton,
  EuiCard,
  EuiCheckableCard,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiImage,
  EuiLink,
  EuiListGroup,
  EuiListGroupItem,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { StepLayout } from '@modules/cui/Step/StepLayout'

import imgServerless from '../../discovery-questions-pages/files/bg-crossed-servers.svg'
import imgStateful from '../../discovery-questions-pages/files/bg-clouds.svg'
import { useFlagsWhenLoaded } from '../../launchdarkly'
import { ResourceType } from '../../discovery-questions-lib/utils'
import docLinks from '../../doclinks/docLinks'

type Props = {
  setResourceType: (value: ResourceType) => void
  resourceType?: ResourceType
  setStep: (value: ResourceType) => void
}

type ResourceOption = Record<
  ResourceType,
  {
    title: NonNullable<ReactNode>
    description: ReactNode
    features: ReactNode[]
  }
>

const RESOURCE_OPTIONS: ResourceOption = {
  serverless: {
    title: (
      <EuiFlexGroup gutterSize='s' alignItems='center'>
        <EuiFlexItem grow={0}>
          <EuiImage src={imgServerless} alt='Elastic cloud serverless icon' size='24px' />
        </EuiFlexItem>
        <EuiFlexItem>
          <FormattedMessage
            id='onboarding-questions-pick-resource-serverless.title'
            defaultMessage='Elastic Cloud Serverless'
          />
        </EuiFlexItem>
      </EuiFlexGroup>
    ),
    description: (
      <FormattedMessage
        defaultMessage='Fully managed, autoscaling servers--for operational simplicity.'
        id='onboarding-questions-pick-resource-serverless.description'
      />
    ),
    features: [
      <FormattedMessage
        defaultMessage='No server, node, or shard management required'
        id='onboarding-questions-pick-resource-serverless-feature0.description'
      />,
      <FormattedMessage
        defaultMessage='Automated scaling, upgrades, and cluster operation'
        id='onboarding-questions-pick-resource-serverless-feature1.description'
      />,
      <FormattedMessage
        defaultMessage='Pay only for compute, storage, and features used'
        id='onboarding-questions-pick-resource-serverless-feature2.description'
      />,
    ],
  },
  stateful: {
    title: (
      <EuiFlexGroup gutterSize='s' alignItems='center'>
        <EuiFlexItem grow={0}>
          <EuiImage src={imgStateful} alt='Elastic cloud hosted icon' size='24px' />
        </EuiFlexItem>
        <EuiFlexItem>
          <FormattedMessage
            id='onboarding-questions-pick-resource-stateful.title'
            defaultMessage='Elastic Cloud Hosted'
          />
        </EuiFlexItem>
      </EuiFlexGroup>
    ),
    description: (
      <FormattedMessage
        defaultMessage='Dedicated servers you manage--for maximum control.'
        id='onboarding-questions-pick-resource-stateful.description'
      />
    ),
    features: [
      <FormattedMessage
        defaultMessage='Configure server, node, and shard resources'
        id='onboarding-questions-pick-resource-stateful-feature0.description'
      />,
      <FormattedMessage
        defaultMessage='Manual sizing and upgrades'
        id='onboarding-questions-pick-resource-stateful-feature1.description'
      />,
      <FormattedMessage
        defaultMessage='Pay flat rate for provisioned resources'
        id='onboarding-questions-pick-resource-stateful-feature2.description'
      />,
    ],
  },
}
const FLEX_STEP_LAYOUT_PROPS = {
  alignItems: 'stretch',
  justifyContent: 'center',
  direction: 'column',
}

const PickResource: FunctionComponent<Props> = ({
  setResourceType,
  resourceType,
  setStep,
}: Props) => {
  const [_, { onboardingResourceType }] = useFlagsWhenLoaded()
  const [value, setValue] = useState<ResourceType | undefined>(resourceType)
  const { euiTheme } = useEuiTheme()

  const orderResourceType: ResourceType[] =
    onboardingResourceType === 'serverless'
      ? [ResourceType.Serverless, ResourceType.Stateful]
      : [ResourceType.Stateful, ResourceType.Serverless]
  return (
    <StepLayout
      title={
        <FormattedMessage
          id='onboarding-questions-pick-resource.title'
          defaultMessage='Which deployment type would you like?'
        />
      }
      description={
        <FormattedMessage
          id='onboarding-questions-pick-resource.description'
          defaultMessage='You can deploy another type later, if needed.'
        />
      }
      footer={
        <EuiFlexGroup alignItems='center' justifyContent='spaceBetween' gutterSize='l'>
          <EuiFlexItem grow={false}>
            {' '}
            <EuiButton
              data-test-id='signup.discovery-questions.pick-resource.continue'
              fill={true}
              disabled={!value}
              onClick={() => {
                if (value) {
                  setResourceType(value)
                  setStep(value)
                }
              }}
            >
              <FormattedMessage id='onboarding-pick-resource.continue' defaultMessage='Continue' />
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiLink
              color='primary'
              target='_blank'
              href={docLinks.serverlessLearnMoreDocLink.userconsole}
            >
              <FormattedMessage
                id='onboarding-pick-resource.learnMore'
                defaultMessage='Learn more'
              />
            </EuiLink>
          </EuiFlexItem>
        </EuiFlexGroup>
      }
      flexGroupChildrenProps={FLEX_STEP_LAYOUT_PROPS}
    >
      {orderResourceType.map((key) => {
        const item = RESOURCE_OPTIONS[key]
        return (
          <EuiFlexItem grow={1}>
            <EuiCheckableCard
              value={key}
              checked={value === key}
              onChange={() => setValue(key as ResourceType)}
              id={`onboarding-questions-pick-resource-${key}`}
              label={
                <EuiCard
                  css={css`
                    p ul {
                      margin-left: 0px;
                    }
                  `}
                  layout='horizontal'
                  paddingSize='none'
                  titleElement='span'
                  title={<div />}
                  display='plain'
                  description={
                    <React.Fragment>
                      <EuiTitle size='xs'>
                        <h2>{item.title}</h2>
                      </EuiTitle>
                      <EuiHorizontalRule margin='s' />
                      <EuiText size='s' color='subdued'>
                        <p>{item.description}</p>
                      </EuiText>
                      <EuiSpacer size='s' />
                      <EuiListGroup flush={true} size='xs'>
                        {item.features.map((desc, idx) => (
                          <EuiListGroupItem
                            color='subdued'
                            iconProps={{ color: euiTheme.colors.success }}
                            wrapText={true}
                            label={<EuiText size='s'>{desc}</EuiText>}
                            iconType='check'
                            key={`onboarding-questions-pick-resource-${key}-feature-${idx}`}
                          />
                        ))}
                      </EuiListGroup>
                    </React.Fragment>
                  }
                />
              }
            />
            <EuiSpacer size='m' />
          </EuiFlexItem>
        )
      })}
    </StepLayout>
  )
}

export default PickResource
